import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug);

  const categoryArticles = useMemo(() => {
    let filtered = articles.filter((a) => a.categorySlug === slug);
    if (sortBy === 'latest') {
      filtered = filtered.sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    } else {
      filtered = filtered.sort((a, b) => b.views - a.views);
    }
    return filtered;
  }, [slug, sortBy]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex flex-col bg-background-950">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-800 mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-600" />
            </div>
            <h1 className="text-xl font-bold text-foreground-300 mb-2">分类未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该分类不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line" /> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: currentCategory.name, href: `/category/${currentCategory.slug}` },
  ];

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteInfo.name}`,
    description: currentCategory.description,
    url: `${siteInfo.url}/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    about: {
      '@type': 'Thing',
      name: currentCategory.name,
      description: currentCategory.description,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          headline: article.title,
          url: `${siteInfo.url}/article/${article.id}`,
          datePublished: article.publishDate,
          author: { '@type': 'Person', name: article.author },
          image: article.image,
        },
      })),
    },
  };

  return (
    <>
      <title>{`${currentCategory.name} - ${siteInfo.name} | 51黑料吃瓜网在线观看`}</title>
      <meta name="description" content={currentCategory.description} />
      <meta
        name="keywords"
        content={`${currentCategory.name},51黑料网,51黑料吃瓜网,${currentCategory.name}爆料,51黑料在线观看`}
      />
      <link rel="canonical" href={`http://jtqzai.cn/category/${currentCategory.slug}`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${currentCategory.name} - ${siteInfo.name}`} />
      <meta property="og:description" content={currentCategory.description} />
      <meta property="og:url" content={`http://jtqzai.cn/category/${currentCategory.slug}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${currentCategory.name} - ${siteInfo.name}`} />
      <meta name="twitter:description" content={currentCategory.description} />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <div className="min-h-screen flex flex-col bg-background-950">
        <header>
          <Navbar />
        </header>

        <main className="pt-20 md:pt-24">
          {/* Category Header */}
          <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-900/50 border-b border-background-800/50">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-7xl mx-auto mt-4">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/20 text-primary-400">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${currentCategory.icon} text-xl`} />
                      </div>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-100">
                      {currentCategory.name}
                    </h1>
                  </div>
                  <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                    {currentCategory.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-500">
                  <span className="px-3 py-1 rounded-full bg-background-800 text-xs">
                    共 {categoryArticles.length} 篇文章
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-8 py-6 md:py-8">
            <div className="max-w-7xl mx-auto">
              {/* Sub-category tabs (all categories as switchable tabs) */}
              <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-thin">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-900 text-foreground-400 hover:text-foreground-200 border border-background-800/50'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`} />
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Sort controls */}
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground-200">文章列表</h2>
                <div className="flex items-center gap-1 p-1 rounded-lg bg-background-900 border border-background-800/50">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'latest'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    <i className="ri-time-line mr-1" />最新
                  </button>
                  <button
                    onClick={() => setSortBy('hot')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'hot'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    <i className="ri-fire-line mr-1" />最热
                  </button>
                </div>
              </div>

              {/* Articles grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group flex flex-col bg-background-900 rounded-xl overflow-hidden border border-background-800/50 hover:border-primary-500/30 transition-all"
                  >
                    <div className="relative h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent" />
                      <div className="absolute top-2 left-2 flex items-center gap-1.5">
                        {article.isExclusive && (
                          <span className="px-1.5 py-0.5 rounded bg-primary-500/90 text-background-50 text-[10px] font-bold">
                            独家
                          </span>
                        )}
                        {article.isHot && (
                          <span className="px-1.5 py-0.5 rounded bg-accent-500/80 text-background-50 text-[10px] font-bold">
                            热
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-sm md:text-base font-bold text-foreground-200 group-hover:text-primary-300 transition-colors line-clamp-2 flex-1">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-800/50">
                        <div className="flex items-center gap-2 text-xs text-foreground-600">
                          <span>{article.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line" />
                            {(article.views / 10000).toFixed(1)}万
                          </span>
                        </div>
                        <span className="text-xs text-primary-400">{article.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {categoryArticles.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-800 mx-auto mb-3">
                    <i className="ri-inbox-line text-2xl text-foreground-600" />
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}